import {useEffect, useState, useCallback ,useRef, useMemo} from 'react'
import * as d3 from 'd3'
import { useHistory} from 'react-router'
import Popup from './PopupMessage'

const radialPoint = (x, y) => {
    return [(y = +y) * Math.cos(x -= Math.PI / 2), y * Math.sin(x)];
}

const TREE_COLORS = {
    root:'#6B4E16',
    gold:'#C9A03C',
    goldBright:'#F2CB58',
    text:'#3B3020',
    textMuted:'#837255',
    link:'#B8A16D',
    alive:'#4A8B5C',
    deceased:'#9A7B2E',
    selected:'#B84040',
    dim:'#C9BFA7'
}

const generationColors = [
    '#6B4E16',
    '#8A6A22',
    '#375A48',
    '#5D5178',
    '#8B5E3C',
    '#47657D',
    '#6B6A3B',
    '#7A564E',
    '#4F6F67',
    '#705B78'
]

const getGenerationColor = depth => generationColors[depth % generationColors.length]

const getNodeColor = d => {
    if(d.depth === 0) return TREE_COLORS.root
    if(d.data.isAlive === false) return TREE_COLORS.deceased
    if(!d.children) return TREE_COLORS.alive
    return getGenerationColor(d.depth)
}

const getNodeRadius = d => {
    if(d.depth === 0) return 13
    if(d.children) return Math.max(5.5, 10 - d.depth * 0.7)
    return d.data.isAlive === false ? 5 : 6.5
}

const getTextSize = d => {
    if(d.depth === 0) return '2.5rem'
    if(d.children) return `${Math.max(1.25, 2.1 - d.depth * 0.13)}rem`
    if(d.depth <= 2) return '1.25rem'
    return '1.5rem'
}

const getTextColor = d => d.data.isAlive === false ? TREE_COLORS.textMuted : getNodeColor(d)

const getLinkOpacity = d => Math.max(0.16, 0.62 - d.target.depth * 0.045)

const getLinkWidth = d => Math.max(1.1, 5.8 - d.target.depth * 0.42)

const getLeafOpacity = (d, scale = 1) => {
    if(scale < 0.45 && d.depth > 3) return 0
    return d.data.isAlive === false ? 0.38 : 0.82
}

const getZoomTextOpacity = (d, scale, selectedMemberId, selectedLineage) => {
    if(selectedMemberId && selectedLineage.has(d.data._id)) return 1
    if(scale < 0.35 && d.depth > 2) return 0
    if(scale < 0.55 && !d.children && d.depth > 3) return 0
    if(scale < 0.8 && !d.children && d.depth > 5) return 0.25
    return 1
}

const Tree = ({familyData,isProfile, isPoint, selectedMemberId}) => {
    const wrapperRef = useRef(null)
    const history = useHistory()
    const [triggerTree, setTriggerTree] = useState(false)
    const [treeError, setTreeError] = useState('')
    
    const familyTree = useCallback(() => {
        if(wrapperRef.current){
            try {
                wrapperRef.current.innerHTML = '';
            const margin = {top: 140, right: 0, bottom: 20, left: 0}
            wrapperRef.current.width = wrapperRef.current.getBoundingClientRect().width
            wrapperRef.current.height = wrapperRef.current.getBoundingClientRect().height
            const innerHeight = 2000 - margin.top - margin.bottom;
            const rect = wrapperRef.current.getBoundingClientRect()
            const containerW = rect.width
            const containerH = rect.height

            const svg = d3.select(wrapperRef.current)
                        .append('svg')
                        .attr('width', containerW)
                        .attr('height', containerH)
                        .attr('encoding', 'UTF-8')
                        .style('touch-action', 'none')
                        .style('cursor', 'grab')

            const defs = svg.append('defs')
            const shadowFilter = defs.append('filter')
                .attr('id', 'treeNodeShadow')
                .attr('x', '-40%')
                .attr('y', '-40%')
                .attr('width', '180%')
                .attr('height', '180%')

            shadowFilter.append('feDropShadow')
                .attr('dx', 0)
                .attr('dy', 4)
                .attr('stdDeviation', 3)
                .attr('flood-color', '#4F3A13')
                .attr('flood-opacity', 0.16)

            const glowFilter = defs.append('filter')
                .attr('id', 'treeSelectedGlow')
                .attr('x', '-80%')
                .attr('y', '-80%')
                .attr('width', '260%')
                .attr('height', '260%')

            glowFilter.append('feDropShadow')
                .attr('dx', 0)
                .attr('dy', 0)
                .attr('stdDeviation', 6)
                .attr('flood-color', TREE_COLORS.goldBright)
                .attr('flood-opacity', 0.55)

            const zoomG = svg.append('g')

            const width = 4000
            const height = 2200
            const g = zoomG.append("g")
            .attr("transform", `translate(${(width/2+ 40)},${(height - 30)})`);
            
            const dataStructure = d3.stratify().id(d => d._id).parentId(d => d.parentId)(familyData)
            const treeLayout = d3.tree().size([(1 * Math.PI), innerHeight])
            const root = treeLayout(dataStructure)
            const selectedNode = selectedMemberId ? root.descendants().find(d => d.data._id === selectedMemberId) : null
            const selectedLineage = new Set()
            let selectedCursor = selectedNode

            while(selectedCursor) {
                selectedLineage.add(selectedCursor.data._id)
                selectedCursor = selectedCursor.parent
            }
            
            
            g.selectAll(".link")
                .data(root.links())
                .enter().append("path")
                .attr('class', 'tree-link')
                .attr('fill', 'none')
                .attr('stroke', TREE_COLORS.link)
                .attr('stroke-linecap', 'round')
                .attr('stroke-linejoin', 'round')
                .attr('stroke-opacity', getLinkOpacity)
                .attr('stroke-width', getLinkWidth)
                .attr("d", d3.linkRadial()
                .angle(d => d.x-Math.PI/2)
                .radius(d => d.y + 10))
                .attr('id',  d => 'link_' + d.target.data._id)
                .attr('stroke-dasharray', function(){
                    const length = this.getTotalLength()
                    return `${length} ${length}`
                })
                .attr('stroke-dashoffset', function(){
                    const length = this.getTotalLength()
                    return length
                })
                .transition()
                .duration(1500)
                .delay(d => d.source.depth * 500)
                .attr('stroke-dashoffset', 0)
        
            const node = g.selectAll(".node")
                .data(root.descendants())
                .enter().append("g")
                .attr("class", d => d.children ? "node node--internal" : "node node--leaf")
                .attr('id', d => 'node_' + d.data._id)
                .attr('transform', d =>  "translate(" + radialPoint(d.x-Math.PI/2, (d.y+10)) + ")");
                node.append("circle")
                .attr('class', 'tree-node-halo')
                .attr("r", d => getNodeRadius(d) + 8)
                .style("fill", getNodeColor)
                .attr('opacity', 0)
                .transition()
                .duration(1500)
                .delay(d => d.depth * 500)
                .attr('opacity', d => d.depth === 0 ? 0.16 : 0.08)

                node.append("circle")
                .attr('class', 'tree-node-dot')
                .attr('id', d => 'circle_' + d.data._id)
                .attr("r", getNodeRadius)
                .style("stroke", '#FFF8E8')
                .style("stroke-width", d => d.depth === 0 ? 3 : 2)
                .style("fill", getNodeColor)
                .attr('filter', 'url(#treeNodeShadow)')
                .attr('opacity', 0)
                .transition()
                .duration(1500)
                .delay(d => d.depth * 500)
                .attr('opacity', 1)
            
            node.append("image")
                .attr('class', 'tree-leaf-icon')
                .attr("xlink:href", d => !d.children ? "/image/leaf.png":"")
                .attr('x', d => d.x > Math.PI/2 ? '-5px': '-10px')
                .attr('y', d => !d.children && d.depth === 1 ? '-55px':"-90px")
                .attr("transform", d => `rotate(${(d.x > Math.PI/2 ? d.x-Math.PI/2  : d.x-Math.PI/2 ) * 180 / Math.PI})`)
                .attr('width', d => d.depth === 1 ? '1rem' : '1.35rem')
                .attr('opacity', 0)
                .transition()
                .duration(1500)
                .delay(d => d.depth * 500)
                .attr('opacity', getLeafOpacity)
                
            const text = node.append("text")
                .attr('class', 'tree-node-label')
                .attr("dy","0.32em")
                .attr('y', (d) => d.depth === 1 ? -1: 2)
                .attr("x", (d) => {
                    if(!d.children){
                        if(d.depth === 1){
                            return 20
                        }
                        if(d.x > Math.PI/2){
                            return 30
                        }else {
                            return -40
                        }
                    }else {
                        return -80
                    }
                })
                // eslint-disable-next-line
                .attr("text-anchor", d => d.x > Math.PI/2 === !d.children ? "middle" : "end")
                .attr("transform", d => d.depth > 1 ? `rotate(${(d.x > Math.PI/2 ? d.x-Math.PI/2 - Math.PI / 2 : d.x-Math.PI/2 + Math.PI / 2) * 180 / Math.PI})` : '')
                .style("font-size", getTextSize)
                .style("font-weight", d => d.depth <= 1 ? 700 : 600)
                .style("letter-spacing", '0')
                .text(d => d.data.firstName)
                .attr('id',  d => 'name_' + d.data._id)
                .style('cursor', 'pointer')
                .style(' z-index', '9999999')
                .attr('fill', getTextColor)
                .on('mouseover', (e, d) => {
                    if(isPoint){
                        g.selectAll('path').style('stroke', TREE_COLORS.dim).style('opacity', 0.18)
                        g.selectAll('text').style('fill', TREE_COLORS.textMuted).style('opacity', 0.24)
                        g.selectAll('.tree-node-dot').style('fill', TREE_COLORS.dim).style('opacity', 0.22)
                        g.selectAll('.tree-node-halo').style('opacity', 0.02)
                        g.selectAll('image').style('opacity', '0.1')
                        while(d){
                            if(!d.data.parentId ) {
                                d3.select(`#name_${d.data._id}`).style('fill',TREE_COLORS.root).style('opacity','1')
                            }
                            if(d.data.parentId !== null){
                                d3.select(`#link_${d.data._id}`).style('stroke',TREE_COLORS.selected).style('opacity','1').style('stroke-width', 4)
                                d3.select(`#circle_${d.data._id}`).style('fill',TREE_COLORS.selected).style('opacity','1')
                                d3.select(`#name_${d.data._id}`).attr('fill',TREE_COLORS.selected).style('opacity','1')
                                .style('font-size', '2.4rem')
                                .transition()
                                .duration(500)
                                .style('font-size', '3.2rem');
                            }
                            d = d.parent
                        }
                    }    
                })
                .on('mouseout', () => {
                    if(isPoint){
                        g.selectAll('path')
                            .style('stroke', TREE_COLORS.link)
                            .style('stroke-opacity', getLinkOpacity)
                            .style('stroke-width', getLinkWidth)
                            .style('opacity','1')
                    text.style('fill', getTextColor)
                        .style('font-size', getTextSize)
                        .style('font-weight', d => d.depth <= 1 ? 700 : 600)
                        .style('opacity','1')
                    g.selectAll('.tree-leaf-icon').style('opacity', getLeafOpacity)
                    g.selectAll('.tree-node-dot').style('fill', getNodeColor).style('opacity','1')
                    g.selectAll('.tree-node-halo').style('fill', getNodeColor).style('opacity', d => d.depth === 0 ? 0.16 : 0.08)
                    }      
            })
            .on('click', (e, d) => {
                if(!isProfile){
                    history.push(`/info/${d.data._id}`)
                } 
            })
            .attr('opacity', 0)
                .transition()
                .duration(1500)
                .delay(d => d.depth * 600)
                .attr('opacity', 1)
            const rootX = width / 2 + 40
            const rootY = height - 30

            const zoom = d3.zoom()
                .scaleExtent([0.12, 3.2])
                .on('zoom', event => {
                    zoomG.attr('transform', event.transform)
                    const scale = event.transform.k
                    text
                        .style('opacity', d => getZoomTextOpacity(d, scale, selectedMemberId, selectedLineage))
                        .style('font-size', d => {
                            if(scale < 0.55 && d.depth > 2) return '1.1rem'
                            return getTextSize(d)
                        })
                    g.selectAll('.tree-leaf-icon')
                        .style('opacity', d => getLeafOpacity(d, scale))
                    svg.style('cursor', 'grabbing')
                })
                .on('end', () => svg.style('cursor', 'grab'))

            svg.call(zoom)
            svg.call(zoom.transform, d3.zoomIdentity
                .translate(containerW / 2 - rootX, containerH / 2 - rootY))

            if(selectedMemberId) {
                if(selectedNode) {
                    const selectedPoint = radialPoint(selectedNode.x-Math.PI/2, selectedNode.y + 10)
                    const selectedX = rootX + selectedPoint[0]
                    const selectedY = rootY + selectedPoint[1]
                    const focusScale = 1.35

                    g.selectAll('path')
                        .style('stroke', d => selectedLineage.has(d.target.data._id) ? TREE_COLORS.gold : TREE_COLORS.link)
                        .style('stroke-opacity', d => selectedLineage.has(d.target.data._id) ? 0.95 : 0.18)
                        .style('stroke-width', d => selectedLineage.has(d.target.data._id) ? 4.5 : 1)

                    g.selectAll('text')
                        .style('opacity', d => selectedLineage.has(d.data._id) ? 1 : 0.35)
                        .style('fill', d => d.data._id === selectedMemberId ? TREE_COLORS.selected : selectedLineage.has(d.data._id) ? TREE_COLORS.gold : TREE_COLORS.textMuted)
                        .style('font-weight', d => d.data._id === selectedMemberId ? '700' : '400')

                    g.selectAll('.tree-node-dot')
                        .style('opacity', d => selectedLineage.has(d.data._id) ? 1 : 0.25)
                        .style('fill', d => d.data._id === selectedMemberId ? TREE_COLORS.selected : selectedLineage.has(d.data._id) ? TREE_COLORS.gold : getNodeColor(d))
                        .style('stroke', d => d.data._id === selectedMemberId ? TREE_COLORS.goldBright : '#FFF8E8')
                        .style('stroke-width', d => d.data._id === selectedMemberId ? 4 : 1)

                    g.selectAll('.tree-node-halo')
                        .style('opacity', d => d.data._id === selectedMemberId ? 0.22 : selectedLineage.has(d.data._id) ? 0.12 : 0.02)

                    g.selectAll('image')
                        .style('opacity', d => selectedLineage.has(d.data._id) ? 1 : 0.18)

                    g.append('circle')
                        .attr('class', 'tree__selected-ring')
                        .attr('cx', selectedPoint[0])
                        .attr('cy', selectedPoint[1])
                        .attr('r', 32)
                        .attr('fill', 'none')
                        .attr('stroke', TREE_COLORS.goldBright)
                        .attr('stroke-width', 4)
                        .attr('stroke-dasharray', '10 6')
                        .attr('filter', 'url(#treeSelectedGlow)')
                        .attr('opacity', 0.95)

                    svg.transition()
                        .duration(750)
                        .call(
                            zoom.transform,
                            d3.zoomIdentity
                                .translate(containerW / 2 - selectedX * focusScale, containerH / 2 - selectedY * focusScale)
                                .scale(focusScale)
                        )
                }
            }

            } catch (error) {
                setTreeError(error.message)
                console.log(error.message.split(':'));
            }
        }},
        [familyData, isPoint, isProfile, history, selectedMemberId],
    )
    useMemo(familyTree,[familyTree, triggerTree])  
  
 useEffect(() => {
    setTriggerTree(true)
  },[])
   
  return (
  <>
    <div className="tree__wrapper" ref={wrapperRef}></div>
    {treeError && <Popup danger><p>{treeError}</p></Popup>}
  </>
  )
    
}

export default Tree
